import { PartsList, ConfigureProductSection, ProductsListSection, SharedAttributesSection } from "./components";
import { ConfiguratorEditorLayout } from "src/components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { Button, TooltipShortcut } from "@itk/mui-components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { configuratorPageK, useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { useNavigate, useParams } from "react-router";
import { applicationActions } from "src/components/application-store";
import { configuratorActions } from "../configurator/configurator-store";
import { useEffect } from "react";
import { configureProductActions } from "./configure-product-store";
import { ROUTES_URL } from "src/utils/constants";
import { useSearchParams } from "react-router-dom";
import { TConfigureProductSearchParams } from "./configure-product-types";

const mapStateToProps = (state: RootState) => ({
  configurationState: state.pageReducer.configureProductReducer.configurationState,
  selectedProjectLineItemId: state.pageReducer.configureProductReducer.selectedProjectLineItemId
});

const ownActions = {
  setWarningAction: applicationActions.setWarningAction,
  deleteConfigurationsRequest: configuratorActions.deleteConfigurationsRequest,
  getConfigurationRequest: configureProductActions.getConfigurationRequest,
  getGenerateConfigurationOrderRequest: configureProductActions.getGenerateConfigurationOrderRequest,
  updateProjectLineItemAttributeRequest: configureProductActions.updateProjectLineItemAttributeRequest,
  clear: configureProductActions.clear,
  getLabelsRequest: configuratorActions.getLabelsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigureProductBase = (props: Props) => {
  const {
    deleteConfigurationsRequest,
    setWarningAction,
    getConfigurationRequest,
    configurationState,
    selectedProjectLineItemId,
    updateProjectLineItemAttributeRequest,
    clear,
    getLabelsRequest,
    getGenerateConfigurationOrderRequest
  } = props;

  const navigate = useNavigate();

  const { configurationId = "", projectId = "" } = useParams<{ configurationId: string; projectId: string }>();
  const [searchParams] = useSearchParams();

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  // Clear redux state on unmount
  useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  // get configuration
  useEffect(() => {
    if (configurationId) {
      getConfigurationRequest(configurationId);
      getLabelsRequest(projectId);
      getGenerateConfigurationOrderRequest(projectId, configurationId);
    }
  }, [configurationId]);

  const handleClose = () => {
    if (!configurationId) return;

    const projectLineItemId = searchParams.get("projectLineItemId" as keyof TConfigureProductSearchParams);

    navigate(ROUTES_URL.CONFIGURATOR.build(projectId), {
      state: {
        configurationId,
        projectLineItemId
      }
    });
  };

  const handleDelete = () => {
    setWarningAction({
      confirm: () => {
        if (!configurationId) return;
        deleteConfigurationsRequest({ configurationIds: [configurationId] }, isSuccess => {
          if (isSuccess) {
            navigate(ROUTES_URL.CONFIGURATOR.build(projectId));
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        }
      },
      variant: "confirmCancel"
    });
  };

  return (
    <ConfiguratorEditorLayout
      sidebar={<ProductsListSection />}
      baseSetup={
        configurationState.loading === "has-loaded" && (
          <SharedAttributesSection
            items={configurationState.data.systemProductConfigurationItems}
            selectedProjectLineItemId={selectedProjectLineItemId}
            updateProjectLineItemAttributeRequest={updateProjectLineItemAttributeRequest}
            onAttributeChanged={() => getGenerateConfigurationOrderRequest(projectId, configurationId)}
          />
        )
      }
      attributePanel={<ConfigureProductSection />}
      partsList={<PartsList />}
      footer={
        <>
          <TooltipShortcut shortcut={{ action: handleDelete, shortCutKey: "s", modifier: "alt" }}>
            <Button
              disabled={configurationState.loading === "is-loading"}
              variant="text"
              color="error"
              sx={{ textDecoration: "underline" }}
              t={configuratorPageT}
              k={configuratorPageK}
              field="deleteConfiguration"
              onClick={handleDelete}
            />
          </TooltipShortcut>
          <TooltipShortcut shortcut={{ action: handleClose, shortCutKey: "g", modifier: "alt" }}>
            <Button variant="contained" t={genericTranslationsT} k={genericTranslationsK} field="saveAndClose" onClick={handleClose} />
          </TooltipShortcut>
        </>
      }
    />
  );
};

export const ConfigureProduct = connect(mapStateToProps, ownActions)(ConfigureProductBase);
